import type { FieldPath, UseFormReturn } from 'react-hook-form';

import { emptyStringIfOnlyNanpCallingCode } from '@/services/phoneUtils';

import {
  MEMBER_FORM_PHONE_FIELDS,
  type ReservationFormInput,
} from '../schema/reservationForm.schema';

const TRIP_PHONE_FIELDS = ['pickupPhone', 'dropoffPhone'] as const;

export type ReservationPhoneFieldName =
  | (typeof MEMBER_FORM_PHONE_FIELDS)[number]
  | (typeof TRIP_PHONE_FIELDS)[number];

export type ReservationPhoneFieldValidationOptions = {
  fieldsToExclude?: readonly ReservationPhoneFieldName[];
};

function hasPhoneDigits(value: string | undefined): boolean {
  return emptyStringIfOnlyNanpCallingCode(value ?? '') !== '';
}

export function getReservationPhoneFieldPaths(
  formValues: ReservationFormInput,
  options?: ReservationPhoneFieldValidationOptions,
): FieldPath<ReservationFormInput>[] {
  const paths: FieldPath<ReservationFormInput>[] = [];
  const excluded = new Set(options?.fieldsToExclude ?? []);

  for (const field of MEMBER_FORM_PHONE_FIELDS) {
    if (excluded.has(field)) continue;
    if (hasPhoneDigits(formValues[field])) {
      paths.push(field);
    }
  }

  const trips = formValues.trips ?? [];
  for (let i = 0; i < trips.length; i++) {
    for (const tripField of TRIP_PHONE_FIELDS) {
      if (excluded.has(tripField)) continue;
      if (hasPhoneDigits(trips[i]?.[tripField])) {
        paths.push(`trips.${i}.${tripField}` as FieldPath<ReservationFormInput>);
      }
    }
  }

  return paths;
}

/** Re-run client-side phone validation after API validation clears trip-level errors. */
export function triggerReservationPhoneFieldValidation(
  form: Pick<UseFormReturn<ReservationFormInput>, 'getValues' | 'trigger'>,
  options?: ReservationPhoneFieldValidationOptions,
): void {
  const paths = getReservationPhoneFieldPaths(form.getValues(), options);
  if (paths.length > 0) {
    void form.trigger(paths);
  }
}
